import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";

import { Can } from "@components/shared";
import { OrganizationUserRolesPermissions } from "@components/Organization";
import { PERMISSIONS } from "@util/permissions";
import { notifyUserFacingError } from "@util/toErrorMessage";
import { fetchUserOrganizations } from "./userOrganizationsApi";

export default function UserOrgMembership({ userId, organizationId }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(() => {
    if (!userId || !organizationId) return;

    setLoading(true);
    Promise.all([
      axios.get(`/users/${userId}/`),
      axios.get(`/organizations/${organizationId}/`),
      fetchUserOrganizations(userId),
    ])
      .then(([userRes, orgRes, orgs]) => {
        setUser(userRes.data.response[0] || null);
        setOrganization(orgRes.data.response[0] || null);
        const row = (orgs || []).find(
          (entry) => String(entry.id) === String(organizationId)
        );
        setMembership(row || null);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setMembership(null);
        notifyUserFacingError(err, toast, { source: "user_org_membership_load" });
      });
  }, [userId, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  function setMembershipActive(nextActive) {
    if (!membership?.organization_user_id) return;

    const path = nextActive
      ? `/organizations/${organizationId}/users/${membership.organization_user_id}/activate/`
      : `/organizations/${organizationId}/users/${membership.organization_user_id}/inactivate/`;

    setBusyKey("membership");
    axios
      .post(path, {})
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        load();
      })
      .catch((err) => {
        setBusyKey(null);
        notifyUserFacingError(err, toast, {
          source: "user_org_membership_status",
        });
      });
  }

  function promoteAdmin() {
    setBusyKey("admin-add");
    axios
      .post(`/organizations/${organizationId}/admins/`, { user_id: userId })
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        load();
      })
      .catch((err) => {
        setBusyKey(null);
        notifyUserFacingError(err, toast, { source: "user_org_promote_admin" });
      });
  }

  function demoteAdmin() {
    if (!membership?.organization_user_admin_id) return;

    setBusyKey("admin-remove");
    axios
      .delete(
        `/organizations/${organizationId}/admins/${membership.organization_user_admin_id}/`
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        load();
      })
      .catch((err) => {
        setBusyKey(null);
        notifyUserFacingError(err, toast, { source: "user_org_demote_admin" });
      });
  }

  function promoteManager() {
    setBusyKey("manager-add");
    axios
      .post(`/organizations/${organizationId}/managers/`, { user_id: userId })
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        load();
      })
      .catch((err) => {
        setBusyKey(null);
        notifyUserFacingError(err, toast, {
          source: "user_org_promote_manager",
        });
      });
  }

  function demoteManager() {
    if (!membership?.organization_user_manager_id) return;

    setBusyKey("manager-remove");
    axios
      .delete(
        `/organizations/${organizationId}/managers/${membership.organization_user_manager_id}/`
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        load();
      })
      .catch((err) => {
        setBusyKey(null);
        notifyUserFacingError(err, toast, {
          source: "user_org_demote_manager",
        });
      });
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!membership) {
    return (
      <Text c="dimmed">{t("user_not_assigned_to_organization")}</Text>
    );
  }

  const userName = user
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
    : userId;
  const orgName = organization?.name || organizationId;
  const membershipActive = membership.status === 2;

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={4}>{t("organization_membership")}</Title>
        <Text size="sm">
          <Anchor component={Link} to={`/users/${userId}`}>
            {userName}
          </Anchor>
          {" · "}
          <Anchor component={Link} to={`/organizations/${organizationId}`}>
            {orgName}
          </Anchor>
        </Text>
      </Stack>

      <Card>
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={5}>{t("cs:organization_singular")}</Title>
            <Badge variant="light">
              {membership.status_formatted ||
                (membershipActive ? t("active") : t("inactive"))}
            </Badge>
          </Group>
          <Can permission={PERMISSIONS.ORGANIZATION_USERS_MANAGE}>
            <Group>
              {membershipActive ? (
                <Button
                  size="xs"
                  variant="light"
                  color="gray"
                  loading={busyKey === "membership"}
                  onClick={() => setMembershipActive(false)}
                >
                  {t("inactive")}
                </Button>
              ) : (
                <Button
                  size="xs"
                  variant="light"
                  color="green"
                  loading={busyKey === "membership"}
                  onClick={() => setMembershipActive(true)}
                >
                  {t("active")}
                </Button>
              )}
            </Group>
          </Can>
        </Stack>
      </Card>

      <Card>
        <Stack gap="sm">
          <Title order={5}>{t("organization_staff")}</Title>
          <Group justify="space-between">
            <Group gap="xs">
              <Text size="sm">{t("organization_admin_plural")}</Text>
              <Badge
                size="sm"
                variant="light"
                color={membership.is_org_admin ? "green" : "gray"}
              >
                {membership.is_org_admin ? t("active") : t("inactive")}
              </Badge>
            </Group>
            <Can permission={PERMISSIONS.ORGANIZATION_ADMINS_MANAGE}>
              {membership.is_org_admin ? (
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  disabled={!membershipActive}
                  loading={busyKey === "admin-remove"}
                  onClick={demoteAdmin}
                >
                  {t("remove")}
                </Button>
              ) : (
                <Button
                  size="xs"
                  variant="light"
                  disabled={!membershipActive}
                  loading={busyKey === "admin-add"}
                  onClick={promoteAdmin}
                >
                  {t("add")}
                </Button>
              )}
            </Can>
          </Group>
          <Group justify="space-between">
            <Group gap="xs">
              <Text size="sm">{t("manager_plural")}</Text>
              <Badge
                size="sm"
                variant="light"
                color={membership.is_org_manager ? "green" : "gray"}
              >
                {membership.is_org_manager ? t("active") : t("inactive")}
              </Badge>
            </Group>
            <Can permission={PERMISSIONS.ORGANIZATION_MANAGERS_MANAGE}>
              {membership.is_org_manager ? (
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  disabled={!membershipActive}
                  loading={busyKey === "manager-remove"}
                  onClick={demoteManager}
                >
                  {t("remove")}
                </Button>
              ) : (
                <Button
                  size="xs"
                  variant="light"
                  disabled={!membershipActive}
                  loading={busyKey === "manager-add"}
                  onClick={promoteManager}
                >
                  {t("add")}
                </Button>
              )}
            </Can>
          </Group>
        </Stack>
      </Card>

      <OrganizationUserRolesPermissions
        organizationId={organizationId}
        userId={userId}
      />
    </Stack>
  );
}
